import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  Link,
  useLocation,
} from "react-router-dom";
import {
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Summarize as SummarizeIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import Login from "./components/Authentication/Login";
import EmailVerify from "./components/Authentication/EmailVerify";
import ResetPassword from "./components/Authentication/ResetPassword";
import Dashboard from "./components/others/Dashboard";
import Admin from "./components/Admin/Admin";
import Products from "./components/Product/Products";
import AddProduct from "./components/Product/AddProduct";
import EditAdmin from "./components/Admin/EditAdmin";
import AddAdmin from "./components/Admin/AddAdmin";
import SettingsIcon from "@mui/icons-material/Settings";
import AdminProfile from "./components/Admin/AdminProfile";
import { useGooglePicker } from "./hooks/useGooglePicker";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReportInquiries from "./components/Report/Report_inquiries";

const drawerWidth = 240;
const collapsedWidth = 60;

function AppContent() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();
  const { pickerLoading } = useGooglePicker();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.get("code");
  }, []);

  const handleClick = (event) => {
    if (!pickerLoading) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    if (!pickerLoading) {
      setAnchorEl(null);
    }
  };

  function isTokenExpired(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const decoded = JSON.parse(atob(base64));
      if (!decoded.exp) return true;

      const expiryTime = decoded.exp * 1000;
      return Date.now() > expiryTime;
    } catch (err) {
      return true;
    }
  }

  useEffect(() => {
    const token = localStorage.getItem("token");
    const adminData = localStorage.getItem("admin");
    const rawPath = location.pathname.replace(/^\/admin-app/, "");
    const publicRoutes = ["/login", "/email-verify", "/reset-password"];

    if (token && adminData) {
      if (isTokenExpired(token)) {
        localStorage.removeItem("token");
        localStorage.removeItem("admin");
        localStorage.removeItem("rowsPerPage");
        setIsAuthenticated(false);
        setAdmin(null);
        navigate("login", { replace: true });
      } else {
        try {
          setIsAuthenticated(true);
          setAdmin(JSON.parse(adminData));
        } catch (error) {
          localStorage.removeItem("token");
          localStorage.removeItem("admin");
          localStorage.removeItem("rowsPerPage");
          setIsAuthenticated(false);
          setAdmin(null);
          navigate("login", { replace: true });
        }
      }
    } else if (!publicRoutes.includes(rawPath)) {
      navigate("login", {
        state: { from: location },
        replace: true,
      });
    }
    setAuthChecked(true);
  }, [navigate, location]);

  const handleLogout = () => {
    handleClose();
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    localStorage.removeItem("rowsPerPage");
    setIsAuthenticated(false);
    setAdmin(null);
    navigate("login", { replace: true });
  };

  if (!authChecked) return null;

  if (!isAuthenticated && !admin) {
    return (
      <Routes>
        <Route
          path="login"
          element={
            <Login
              setIsAuthenticated={setIsAuthenticated}
              setAdmin={setAdmin}
            />
          }
        />
        <Route path="email-verify" element={<EmailVerify />} />
        <Route path="reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        style={{
          background: "#26A69A",
          zIndex: 1300,
          animation: "fadeIn 0.5s ease-in-out",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(!drawerOpen)}
              style={{ color: "#FFFFFF", marginRight: "16px" }}
              disabled={pickerLoading}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              style={{ flexGrow: 1, color: "#FFFFFF", fontWeight: 600 }}
            >
              Admin Panel
            </Typography>
          </Toolbar>
        </div>
        <div style={{ marginRight: "15px" }}>
          <IconButton onClick={handleClick} sx={{ color: "white" }}>
            <SettingsIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              style: {
                borderRadius: 8,
                minWidth: 150,
              },
            }}
          >
            <MenuItem component={Link} to="admin/profile" onClick={handleClose}>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </div>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerOpen ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          transition: "width 0.3s ease-in-out",
          "& .MuiDrawer-paper": {
            width: drawerOpen ? drawerWidth : collapsedWidth,
            background: "#FFFFFF",
            boxShadow: "2px 0 8px rgba(0, 0, 0, 0.1)",
            boxSizing: "border-box",
            overflowX: "hidden",
            position: "fixed",
            animation: "fadeIn 0.5s ease-in-out",
          },
        }}
      >
        <Toolbar />
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <List>
            <ListItem
              button
              onClick={() => navigate("dashboard")}
              sx={{
                background:
                  location.pathname === "dashboard" ? "#E0F2F1" : "transparent",
                "&:hover": { background: "#E0F2F1" },
                transition: "background 0.2s",
              }}
            >
              <ListItemIcon>
                <DashboardIcon style={{ color: "#26A69A" }} />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Dashboard"
                  style={{ color: "#263238", cursor: "pointer" }}
                />
              )}
            </ListItem>
            <ListItem
              button
              onClick={() => navigate("admin")}
              sx={{
                background:
                  location.pathname === "admin" ? "#E0F2F1" : "transparent",
                "&:hover": { background: "#E0F2F1" },
                transition: "background 0.2s",
              }}
            >
              <ListItemIcon>
                <PeopleIcon style={{ color: "#26A69A" }} />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Admin"
                  style={{ color: "#263238", cursor: "pointer" }}
                />
              )}
            </ListItem>
            <ListItem
              button
              onClick={() => navigate("products")}
              sx={{
                background:
                  location.pathname === "products" ? "#E0F2F1" : "transparent",
                "&:hover": { background: "#E0F2F1" },
                transition: "background 0.2s",
              }}
            >
              <ListItemIcon>
                <InventoryIcon style={{ color: "#26A69A" }} />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Products"
                  style={{ color: "#263238", cursor: "pointer" }}
                />
              )}
            </ListItem>
            <ListItem
              button
              onClick={() => navigate("report-inquiries")}
              sx={{
                background:
                  location.pathname === "report-inquiries"
                    ? "#E0F2F1"
                    : "transparent",
                "&:hover": { background: "#E0F2F1" },
                transition: "background 0.2s",
              }}
            >
              <ListItemIcon>
                <SummarizeIcon style={{ color: "#26A69A" }} />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Report Inquiries"
                  style={{ color: "#263238", cursor: "pointer" }}
                />
              )}
            </ListItem>
          </List>
          <div style={{ flexGrow: 1 }} />
          <List>
            <ListItem
              button
              onClick={handleLogout}
              sx={{
                "&:hover": { background: "#FFEBEE" },
                transition: "background 0.2s",
              }}
            >
              <ListItemIcon>
                <LogoutIcon style={{ color: "#FF6F61" }} />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Logout"
                  style={{ color: "#263238", cursor: "pointer" }}
                />
              )}
            </ListItem>
          </List>
        </div>
      </Drawer>
      <div
        component="main"
        style={{
          flexGrow: 1,
          width: "100%",
          padding: "24px",
          marginLeft: 0,
        }}
      >
        <Toolbar />
        <Routes>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="admin" element={<Admin />} />
          <Route path="admin/edit/:id" element={<EditAdmin />} />
          <Route path="admin/add" element={<AddAdmin />} />
          <Route path="admin/profile" element={<AdminProfile />} />
          <Route path="products" element={<Products />} />
          <Route path="products/add" element={<AddProduct />} />
          <Route path="report-inquiries" element={<ReportInquiries />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
      <ToastContainer />
    </Router>
  );
}

export default App;
